import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { addMonths, startOfMonth, endOfMonth, addYears, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import { format, isValid } from 'date-fns';
import { OrangeButton } from './Buttons';
import { XMarkIcon } from "@heroicons/react/20/solid";
import { getIsMobile } from './Responsive';

const css = `
  .rdp-table {
    border-collapse: separate;
    border-spacing: 0 2px;
  }
  .my-selected:not([disabled]) {
    font-weight: semibold; 
    background-color: rgba(0,0,0,0.1);
    color: rgb(249 115 22);
  }
  .my-selected:hover:not([disabled]) {
    border-color: rgb(249 115 22);
    color: rgb(249 115 22);
  }
  .my-today { 
    font-weight: bold;
    text-decoration: underline;
  }
  .rdp-month {
    border-radius: 12px;
    box-shadow: 0px 0px 10px 3px rgba(0,0,0,0.1);
    overflow: hidden;
  }
  .rdp-row{
    margin: 1px 1px;
  }
  .rdp-caption {
    background-color: rgb(249 115 22);
    color: white;
  }
  .rdp-caption_label {
    font-weight: 300;
    text-transform: capitalize;
  }
  .rdp-head {
    box-shadow: 0px -2px 10px 0px rgba(0,0,0,0.1);
  }
  .rdp-head_cell{
    font-weight: 500;
  }
  .rdp-day_range_start:not([disabled]), .rdp-day_range_end:not([disabled]) {
    background-color: rgb(249 115 22);
    color: white;
  }
  .rdp-day_outside, .rdp-day_outside:not([disabled]) {
    color: rgba(0,0,0,0.2);
    pointer-events: none;
  }
  .rdp-day_outside:not([disabled]) {
    background: none;
  }
`;

const buttonsClasses = `hover:underline hover:text-[#d16113]`

export default function DateRangePicker({onChange, selectedValue}) {
    const isMobile = getIsMobile();
    const { t } = useTranslation();

    const [range, setRange] = useState(selectedValue || {from: null, to: null});
    const today = new Date();
    const [numberOfMonths, setNumberOfMonths] = useState(isMobile ? 1 : 2);

    const startOfThisYear = startOfYear(today);
    const endOfThisYear = endOfYear(today);

    const lastYear = addYears(today, -1);
    const startOfLastYear = startOfYear(lastYear);
    const endOfLastYear = endOfYear(lastYear);

    const startOfThisMonth = startOfMonth(today);
    const endOfThisMonth = endOfMonth(today);
    const nextMonth = addMonths(today, 1);
    const [view, setView] = useState(nextMonth);

    useEffect(() => {
        setView(selectedValue.from ? selectedValue.from : today);
    }, []);

    const handleChange = (newRange) => {
        setRange(newRange);
        onChange(newRange);
    }

    const handleThisMonth = (event) => {
        event.preventDefault();
        handleChange({"date": {"from": startOfThisMonth, "to": endOfThisMonth}});
    };

    const handleThisYear = (event) => {
        event.preventDefault();
        handleChange({"date": {"from": startOfThisYear, "to": endOfThisYear}});
    };

    const handleLastYear = (event) => {
        event.preventDefault();
        handleChange({"date": {"from": startOfLastYear, "to": endOfLastYear}});
    };
    
    const Enregistrer = (event) => {
        event.preventDefault();
        onChange({ date: range })
    };

    const handleDelete = (event) => {
        event.preventDefault();
        handleChange({"date": {"from": null, "to": null}});
    }

    const footer = (
        <div>
                {range && isValid(range.from) && (
                    <div className="flex justify-center items-center gap-2 bg-gray-200 p-1 rounded-lg my-2">
                        {isValid(range.from) && (
                            <p className="text-gray-800 text-sm font-medium">
                                <span className="text-xs text-gray-500">{t("account.starting_from")} </span>
                                {format(range.from, 'dd/MM/yyyy')}
                            </p>
                        )}
                        {isValid(range.to) && (
                            <p className="text-gray-800 text-sm font-medium">
                                <span className="text-xs text-gray-500">{t("account.to")} </span>
                                {format(range.to, 'dd/MM/yyyy')}
                            </p>
                        )}
                        <XMarkIcon 
                            onClick={handleDelete}
                            className='w-6 h-6 text-gray-800 hover:bg-gray-400 cursor-pointer rounded-md'/>
                    </div>
                )}
            <div className='flex flex-col sm:flex-row gap-4 sm:gap-8 text-[#F97316] text-lg font-medium pb-5'>
                <div className='flex justify-between gap-4 sm:gap-8'>
                    <button onClick={handleThisMonth} className={buttonsClasses}>
                        {t("account.this_month")}
                    </button>
                    <button onClick={handleThisYear} className={buttonsClasses}>
                        {t("account.this_year")}
                    </button>
                    <button onClick={handleLastYear} className={buttonsClasses}>
                        {t("account.last_year")}
                    </button>
                </div>
                {range.from != null &&
                    <OrangeButton onClick={Enregistrer} icon="check" type="submit" className="justify-center">
                        {t("account.register")}
                    </OrangeButton>
                }
            </div>
        </div>
    );

    return (
        <div className='day-picker flex justify-center items-center flex-col'>
            <style>{css}</style>
            <DayPicker
            id="dateRangePicker"
            mode="range"
            required
            modifiersClassNames={{
                selected: 'my-selected',
                today: 'my-today'
            }}
            defaultMonth={today}
            selected={range}
            onSelect={(planning) =>{
                if (planning) {
                    setRange(planning);
                } else {
                    setRange({from: null, to:null})
                }
            }}
            numberOfMonths={numberOfMonths}
            showOutsideDays
            fixedWeeks
            locale={fr}
            month={view}
            onMonthChange={setView}
            />
            {footer}
        </div>
    );
}